import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper';

export interface TableBankingLoanRepayment {
  id: number;
  group_id: number;
  member_id: number;
  table_banking_loan_id: number;
  amount: number;
  payment_date: string;
  payment_method: 'WALLET' | 'CASH' | 'MPESA' | 'BANK' | 'top_up_loan';
  description?: string;
  transaction_reference?: string;
  status: string;
  processed_by?: number;
  created_at: string;
  updated_at: string;
  member?: {
    id: number;
    name: string;
    phone_number: string;
  };
  table_banking_loan?: {
    id: number;
    principal_amount: number;
    loan_balance: number;
    status: string;
    member: {
      id: number;
      name: string;
    };
    table_banking: {
      id: number;
      name: string;
      interest_rate: number;
      penalty_amount : number
    };
  };
  processed_by_user?: {
    id: number;
    name: string;
  };
}

export interface RepaymentFilters {
  search?: string;
  member_id?: number;
  table_banking_loan_id?: number;
  payment_method?: string;
  status?: string;
  repayment_date?: string;
  amount_min?: number;
  amount_max?: number;
  quick_filter?: 'today' | 'this_week' | 'this_month';
  page?: number;
  per_page?: number;
}

export interface RepaymentAnalytics {
  summary: {
    total_repayments: number;
    repayment_count: number;
    average_repayment: number;
  };
  payment_methods: Record<string, {
    count: number;
    total: number;
  }>;
  daily_repayments: Array<{
    date: string;
    total: number;
  }>;
  top_members: Array<{
    member_id: number;
    total_paid: number;
    payment_count: number;
    member: {
      id: number;
      name: string;
    };
  }>;
  recent_repayments: TableBankingLoanRepayment[];
  period: {
    start_date: string;
    end_date: string;
  };
}

export interface CreateRepaymentData {
  table_banking_loan_id: number;
  amount: number;
  payment_date: string;
  payment_method: 'WALLET' | 'CASH' | 'MPESA' | 'BANK' | 'top_up_loan';
  description?: string;
  transaction_reference?: string;
}

// Hook to fetch loan repayments with filters
export const useTableBankingLoanRepayments = (filters: RepaymentFilters = {}) =>
  useQuery({
    queryKey: ['table-banking-loan-repayments', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/tablebanking/repayments?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

// Hook to fetch a single repayment
export const useTableBankingLoanRepayment = (id: number) => {
  return useQuery({
    queryKey: ['table-banking-loan-repayment', id],
    queryFn: async () => {
      const { data } = await api.get(`/tablebanking/repayments/${id}`);
      return data;
    },
    enabled: !!id,
  });
};



// Hook to fetch repayments for a specific loan
export const useLoanRepaymentsByLoan = (loanId: number) => {
  return useQuery({
    queryKey: ['table-banking-loan-repayments-by-loan', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/tablebanking/repayments/loan/${loanId}`);
      console.log(data);
      return data;
    },
    enabled: !!loanId,
  });
};

// Hook to create a new repayment
// export const useCreateRepayment = () => {
//   const queryClient = useQueryClient();
  
//   return useMutation({
//     mutationFn: async (repaymentData: CreateRepaymentData) => {
//       const { data } = await api.post('/loan-repayments', repaymentData);
//       return data;
//     },
//     onSuccess: (data) => {
//       // Invalidate relevant queries
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments'] });
//       queryClient.invalidateQueries({ queryKey: ['member-loans'] });
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments-by-loan'] });
      
//       toast.success('Repayment recorded successfully');
//     },
//     onError: (error) => {
//       toast.error(getApiErrorMessage(error));
//     },
//   });
// };

// // Hook to update a repayment
// export const useUpdateRepayment = () => {
//   const queryClient = useQueryClient();
  
//   return useMutation({
//     mutationFn: async ({ id, ...repaymentData }: { id: number } & Partial<CreateRepaymentData>) => {
//       const { data } = await api.put(`/loan-repayments/${id}`, repaymentData);
//       return data;
//     },
//     onSuccess: (data) => {
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments'] });
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayment', data.data.id] });
//       queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments-by-loan'] });
      
//       toast.success('Repayment updated successfully');
//     },
//     onError: (error) => {
//       toast.error(getApiErrorMessage(error));
//     },
//   });
// };

// Hook to delete a repayment
// export const useDeleteRepayment = () => {
//   const queryClient = useQueryClient();
  
//   return useMutation({
//     mutationFn: async (id: number) => {
//       const { data } = await api.delete(`/loan-repayments/${id}`);
//       return data;
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments'] });
//       queryClient.invalidateQueries({ queryKey: ['tableBankingLoans'] });
//       queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments-by-loan'] });
      
//       toast.success('Repayment deleted successfully');
//     },
//     onError: (error) => {
//       toast.error(getApiErrorMessage(error));
//     },
//   });
// };

// Hook to pay loan via M-Pesa
export const usePayLoanViaMpesa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      amount: number;
      notes?: string;
      phone_number?: string;
      table_banking_loan_id: number;
    }) => {
      const { data: response } = await api.post('/tablebanking/repay-via-mpesa', data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments'] });
      queryClient.invalidateQueries({ queryKey: ['member-loans'] });
      queryClient.invalidateQueries({ queryKey: ['table-banking-loan-repayments-by-loan'] });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      toast.success('Loan payment initiated. Please check your phone for M-Pesa prompt.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to initiate loan payment');
    },
  });
};

// Export payment method options for UI components
export const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash', color: 'bg-green-100 text-green-800' },
  { value: 'mobile_money', label: 'M-Pesa', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'bank_transfer', label: 'Bank Transfer', color: 'bg-purple-100 text-purple-800' },
  { value: 'digital_wallet', label: 'Wallet', color: 'bg-blue-100 text-blue-800' },
  { value: 'top_up_loan', label: 'Top-up Loan', color: 'bg-orange-100 text-orange-800' },
];

// Utility function to get payment method badge color
export const getPaymentMethodBadge = (method: string) => {
  const option = PAYMENT_METHOD_OPTIONS.find(opt => opt.value === method);
  return option?.color || 'bg-gray-100 text-gray-800';
};

// Utility function to format currency
export const formatCurrency = (amount: number | string) => {
  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === '') return 'Ksh 0';
  
  // Convert to number and handle edge cases
  let numAmount: number;
  if (typeof amount === 'string') {
    // Remove any non-numeric characters except decimal point and minus sign
    const cleanedAmount = amount.replace(/[^\d.-]/g, '');
    numAmount = parseFloat(cleanedAmount);
  } else {
    numAmount = amount;
  }
  
  // Check if the conversion resulted in a valid number
  if (isNaN(numAmount) || !isFinite(numAmount)) return 'Ksh 0';
  
  // Ensure we have proper decimal precision (round to 2 decimal places)
  numAmount = Math.round(numAmount * 100) / 100;
  
  return `Ksh ${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
}; 